/**
 * SouvenirIcons.ts
 * Handcrafted 2.5D illustrated inline SVG iconography for all 5 US State Souvenirs:
 * - NY: Statue of Liberty Mini Figurine
 * - CA: Golden Gate Bridge Miniature
 * - TX: Vintage Longhorn & Stetson Hat
 * - FL: Space Shuttle & Flamingo Diorama
 * - WA: Space Needle Desktop Model
 */

export const SOUVENIR_SVG_ICONS: Record<string, string> = {
  /**
   * NY: Statue of Liberty (souvenir_ny_statue_liberty)
   * Verdigris green copper statue with rayed radiate crown, blazing golden torch flame,
   * tablet of law, on a tiered stone pedestal.
   */
  souvenir_ny_statue_liberty: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sv_ny_copper" x1="18" y1="12" x2="46" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#86efac"/>
        <stop offset="50%" stop-color="#34d399"/>
        <stop offset="100%" stop-color="#059669"/>
      </linearGradient>
      <linearGradient id="sv_ny_pedestal" x1="16" y1="46" x2="48" y2="58" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#475569"/>
        <stop offset="100%" stop-color="#1e293b"/>
      </linearGradient>
    </defs>
    <!-- Base Pedestal -->
    <rect x="18" y="52" width="28" height="6" rx="2" fill="url(#sv_ny_pedestal)" stroke="#334155" stroke-width="1"/>
    <rect x="22" y="46" width="20" height="6" rx="1" fill="#334155" stroke="#475569" stroke-width="0.8"/>
    <!-- Robed Torso & Body -->
    <path d="M26 26 L 38 26 L 41 46 L 23 46 Z" fill="url(#sv_ny_copper)" stroke="#047857" stroke-width="1"/>
    <!-- Robe Folds -->
    <path d="M28 26 L 27 46 M 34 26 L 36 46 M 31 30 L 32 46" stroke="#065f46" stroke-width="1"/>
    <!-- Left Hand Holding Tablet of Law -->
    <rect x="21" y="32" width="5" height="8" rx="0.5" fill="#a7f3d0" stroke="#047857" stroke-width="0.8" transform="rotate(10 21 32)"/>
    <!-- Right Arm Raised High Holding Blazing Torch -->
    <path d="M36 28 L 44 14" stroke="url(#sv_ny_copper)" stroke-width="3" stroke-linecap="round"/>
    <!-- Torch Handle & Golden Flame -->
    <rect x="42" y="11" width="4" height="4" fill="#d97706" rx="0.5"/>
    <path d="M44 4 C 47 7, 49 10, 44 12 C 41 10, 41 7, 44 4 Z" fill="#fbbf24" stroke="#f59e0b" stroke-width="0.6"/>
    <!-- Head & Face -->
    <ellipse cx="32" cy="20" rx="4.5" ry="5.5" fill="#6ee7b7" stroke="#047857" stroke-width="0.8"/>
    <!-- 7-Rayed Radiate Crown -->
    <path d="M26 17 L 22 13 M 28 16 L 26 10 M 31 15 L 32 8 M 34 15 L 36 10 M 36 16 L 40 12" stroke="#34d399" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,

  /**
   * CA: Golden Gate Bridge (souvenir_ca_golden_gate)
   * International orange Art Deco suspension tower, sweeping cables, marine waves, Pacific sun.
   */
  souvenir_ca_golden_gate: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sv_ca_tower" x1="20" y1="8" x2="44" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb7185"/>
        <stop offset="50%" stop-color="#e11d48"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </linearGradient>
    </defs>
    <!-- Bay Water Waves & Horizon -->
    <path d="M6 46 Q 18 42 32 46 Q 46 50 58 46 L 58 56 L 6 56 Z" fill="#0369a1"/>
    <path d="M6 50 Q 20 46 32 50 Q 48 54 58 50" stroke="#38bdf8" stroke-width="1.2" fill="none"/>
    <!-- Main Suspension Cables (Sweeping parabolas) -->
    <path d="M6 24 Q 28 40 58 24" stroke="#f43f5e" stroke-width="2" fill="none"/>
    <!-- Vertical Suspenders -->
    <line x1="12" y1="28" x2="12" y2="44" stroke="#f43f5e" stroke-width="0.8"/>
    <line x1="18" y1="33" x2="18" y2="44" stroke="#f43f5e" stroke-width="0.8"/>
    <line x1="42" y1="35" x2="42" y2="44" stroke="#f43f5e" stroke-width="0.8"/>
    <line x1="48" y1="30" x2="48" y2="44" stroke="#f43f5e" stroke-width="0.8"/>
    <line x1="54" y1="26" x2="54" y2="44" stroke="#f43f5e" stroke-width="0.8"/>
    <!-- Roadway Deck Truss -->
    <rect x="6" y="42" width="52" height="4" fill="#881337" stroke="#4c0519" stroke-width="0.8"/>
    <!-- Art Deco Bridge Tower (Center-left) -->
    <rect x="26" y="10" width="5" height="40" fill="url(#sv_ca_tower)"/>
    <rect x="35" y="10" width="5" height="40" fill="url(#sv_ca_tower)"/>
    <!-- Cross Struts / Portals -->
    <rect x="29" y="14" width="8" height="3" fill="#be123c"/>
    <rect x="29" y="24" width="8" height="3" fill="#be123c"/>
    <rect x="29" y="34" width="8" height="3" fill="#be123c"/>
    <!-- Tower Pinnacle Tips -->
    <polygon points="26,10 28.5,6 31,10" fill="#f43f5e"/>
    <polygon points="35,10 37.5,6 40,10" fill="#f43f5e"/>
  </svg>`,

  /**
   * TX: Longhorn & Stetson Hat (souvenir_tx_longhorn)
   * Wide sweeping polished longhorn steer horns, leather cowboy hat, silver Lone Star badge.
   */
  souvenir_tx_longhorn: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sv_tx_horn" x1="8" y1="12" x2="56" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="40%" stop-color="#fef08a"/>
        <stop offset="80%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="sv_tx_hat" x1="16" y1="24" x2="48" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#d97706"/>
        <stop offset="70%" stop-color="#b45309"/>
        <stop offset="100%" stop-color="#78350f"/>
      </linearGradient>
    </defs>
    <!-- Sweeping Steer Longhorns (Left and Right arc) -->
    <!-- Left Horn -->
    <path d="M32 28 C 24 26, 12 20, 6 12 C 9 17, 18 27, 30 30 Z" fill="url(#sv_tx_horn)" stroke="#451a03" stroke-width="1"/>
    <!-- Right Horn -->
    <path d="M32 28 C 40 26, 52 20, 58 12 C 55 17, 46 27, 34 30 Z" fill="url(#sv_tx_horn)" stroke="#451a03" stroke-width="1"/>
    <!-- Stetson Cowboy Hat Crown -->
    <path d="M22 34 C 22 24, 27 22, 32 24 C 37 22, 42 24, 42 34 Z" fill="url(#sv_tx_hat)" stroke="#451a03" stroke-width="1.2"/>
    <!-- Hat Crease indent in top -->
    <path d="M27 24 Q 32 27 37 24" stroke="#78350f" stroke-width="1.5" fill="none"/>
    <!-- Leather Hat Band -->
    <rect x="22" y="32" width="20" height="3" fill="#451a03"/>
    <!-- Silver Lone Star Buckle -->
    <polygon points="32,32 33,34 35,34 33.5,35 34,37 32,36 30,37 30.5,35 29,34 31,34" fill="#f8fafc" stroke="#cbd5e1" stroke-width="0.3"/>
    <!-- Curved Stetson Brim -->
    <path d="M10 40 C 14 36, 22 35, 32 35 C 42 35, 50 36, 54 40 C 48 44, 16 44, 10 40 Z" fill="url(#sv_tx_hat)" stroke="#451a03" stroke-width="1.2"/>
    <path d="M12 40 Q 32 44 52 40" stroke="#fde68a" stroke-width="0.8" fill="none"/>
  </svg>`,

  /**
   * FL: Space Shuttle & Flamingo (souvenir_fl_rocket_flamingo)
   * Sleek aerospace rocket shuttle model standing next to a graceful pink flamingo and palm frond.
   */
  souvenir_fl_rocket_flamingo: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Stand Platform -->
    <ellipse cx="32" cy="54" rx="24" ry="6" fill="#1e293b" stroke="#38bdf8" stroke-width="1"/>
    <!-- Space Shuttle / Rocket (Left) -->
    <path d="M20 12 C 18 16, 16 28, 16 44 L 26 44 C 26 28, 24 16, 22 12 Z" fill="#f8fafc" stroke="#334155" stroke-width="1"/>
    <polygon points="21,6 19,12 23,12" fill="#ef4444"/>
    <polygon points="16,36 10,44 16,44" fill="#334155"/>
    <polygon points="26,36 32,44 26,44" fill="#334155"/>
    <circle cx="21" cy="22" r="1.8" fill="#38bdf8"/>
    <!-- Graceful Pink Flamingo (Right) -->
    <!-- Slender Legs -->
    <line x1="42" y1="38" x2="42" y2="52" stroke="#fb7185" stroke-width="1.2"/>
    <line x1="42" y1="44" x2="46" y2="48" stroke="#fb7185" stroke-width="1.2"/>
    <!-- Body Oval -->
    <ellipse cx="42" cy="35" rx="7" ry="5" fill="#f43f5e" stroke="#e11d48" stroke-width="0.8"/>
    <!-- S-Curve Neck -->
    <path d="M47 34 Q 50 28 48 22 Q 46 18 43 18" stroke="#fb7185" stroke-width="2.2" stroke-linecap="round" fill="none"/>
    <!-- Head & Curved Beak -->
    <ellipse cx="42" cy="18" rx="2.5" ry="2" fill="#f43f5e"/>
    <path d="M40 18 Q 37 19 36 22" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="43" cy="17" r="0.6" fill="#ffffff"/>
  </svg>`,

  /**
   * WA: Space Needle Desktop Model (souvenir_wa_space_needle)
   * Seattle Space Needle miniature: flying saucer observation deck, tripod legs, spire, Mt. Rainier.
   */
  souvenir_wa_space_needle: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Mt. Rainier in Backdrop (Snow-capped mountain silhouette) -->
    <polygon points="8,48 24,28 36,48" fill="#334155"/>
    <polygon points="20,33 24,28 28,33 26,35 24,33 22,35" fill="#f8fafc"/>
    <polygon points="28,48 44,24 58,48" fill="#475569"/>
    <polygon points="40,30 44,24 49,31 46,33 44,31 42,33" fill="#f8fafc"/>
    <!-- Base Plate -->
    <ellipse cx="32" cy="54" rx="20" ry="5" fill="#0f172a" stroke="#0284c7" stroke-width="1"/>
    <!-- Tripod Core & Legs -->
    <line x1="32" y1="26" x2="32" y2="52" stroke="#94a3b8" stroke-width="2"/>
    <line x1="26" y1="52" x2="31" y2="34" stroke="#cbd5e1" stroke-width="1.5"/>
    <line x1="38" y1="52" x2="33" y2="34" stroke="#cbd5e1" stroke-width="1.5"/>
    <!-- Waist Ring / Elevator track -->
    <ellipse cx="32" cy="38" rx="4" ry="1.5" fill="#38bdf8"/>
    <!-- Observation Flying Saucer Deck -->
    <ellipse cx="32" cy="24" rx="14" ry="4" fill="#f8fafc" stroke="#334155" stroke-width="1"/>
    <ellipse cx="32" cy="22" rx="10" ry="2.5" fill="#f97316"/>
    <!-- Needle Spire -->
    <line x1="32" y1="6" x2="32" y2="21" stroke="#f8fafc" stroke-width="1.8"/>
    <circle cx="32" cy="6" r="1.2" fill="#ef4444"/>
  </svg>`,
};
